import React, { useEffect, useState } from 'react';
import InfoDialog from '../../../components/shared/InfoDialog';
import { FormControl, FormControlLabel, RadioGroup, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomRadio from '../../../components/forms/theme-elements/CustomRadio';
import { IconCheck, IconCross, IconX } from '@tabler/icons-react';
import useColor from '../../../hooks/site/useColor';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import { API_USERS } from '../../../api/users';
import dayjs from 'dayjs';
import { getOnlyData } from '../../../utils';
import useToast from '../../../hooks/useToast';
import { updateUserResult } from '../../../store/apps/usersSlice';
import { useDispatch } from 'react-redux';

const ENUM_BLOCK_STATE = {
  allow: 'allow',
  prohibit: 'prohibit'
};

export default function BlockDeposit({ open, closeModal, user }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { successToast } = useToast();
  const { success, error } = useColor();
  const [blockState, setBlockState] = useState(ENUM_BLOCK_STATE.allow);
  const [autoBlock, setAutoBlock] = useState(false);
  const [lockedTo, setLockedTo] = useState('');
  const userId = user.userId;

  const title = `${t('Blocking user deposits')} ${user?.login}`;

  const getDate = (date) => getOnlyData(dayjs(date), '', 'DD.MM.YYYY HH:mm:ss');

  const getDepositLock = () => {
    API_USERS.info.deposit.lock
      .get({ userId })
      .then((e) => {
        const data = e.data.data;
        setBlockState(data.enabled ? ENUM_BLOCK_STATE.allow : ENUM_BLOCK_STATE.prohibit);
        setLockedTo(data.lockedTo);
        setAutoBlock(data.enabled);

        dispatch(updateUserResult({ id: userId, dataIndx: 5, value: data.enabled }));
        dispatch(updateUserResult({ id: userId, dataIndx: 6, value: data.lockedTo }));
      })
      .catch(console.error);
  };

  useEffect(() => {
    if (!userId) return;
    if (!open) return;

    getDepositLock();
  }, [userId, open]);

  const onSaveClick = () => {
    const autoBlockDep = blockState == ENUM_BLOCK_STATE.allow;
    API_USERS.info.deposit.lock
      .set({ userId, enabled: autoBlockDep })
      .then((e) => {
        successToast('Information updated successfully');

        getDepositLock();
        closeModal();
      })
      .catch(console.error);
  };

  return (
    <InfoDialog
      open={open}
      onClose={closeModal}
      title={title}
      maxWidth="sm"
      onSaveClick={onSaveClick}>
      <Stack gap={2}>
        <Stack gap={1}>
          <Typography fontWeight={600}>{t('Current state')}</Typography>
          <Stack gap={0.5} pl={4}>
            {autoBlock ? (
              <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                <IconCheck color={success.main} />
                <Typography>{t('Automatic blocking is enabled')}</Typography>
              </Stack>
            ) : (
              <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                <IconX color={error.main} />
                <Typography>{t('Automatic blocking is prohibited')}</Typography>
              </Stack>
            )}

            {lockedTo ? (
              <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                <IconX color={error.main} />
                <Typography>
                  {t('Current blocking up to')} {getDate(lockedTo)}
                </Typography>
              </Stack>
            ) : (
              <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                <IconCheck color={success.main} />
                <Typography>{t('There is no current blocking')}</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack gap={1}>
          <Typography fontWeight={600}>{t('Set a new auto lock state')}:</Typography>
          <FormControl component="fieldset">
            <RadioGroup value={blockState} onChange={(e) => setBlockState(e.target.value)}>
              <FormControlLabel
                value={ENUM_BLOCK_STATE.allow}
                control={<CustomRadio />}
                label={
                  <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                    <Typography>{t('Allow')}</Typography>
                    <CusTooltip
                      text={
                        'Blocking of deposit creation will be set automatically according to the setting casino conditions'
                      }
                    />
                  </Stack>
                }
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                value={ENUM_BLOCK_STATE.prohibit}
                control={<CustomRadio />}
                label={
                  <Stack direction={'row'} gap={0.5} alignItems={'center'}>
                    <Typography>{t('Prohibit')}</Typography>
                    <CusTooltip
                      text={
                        'The player can create an unlimited number of unpaid requests for replenishment'
                      }
                    />
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </Stack>
    </InfoDialog>
  );
}
